import { ProductTag } from '@medusajs/medusa';
import _ from 'lodash';
import Link from 'next/link';
import { Fragment, useMemo, RefObject } from 'react';

import { colorsTranslations, metadataKeyMap } from '@/utils/const';

import { Product } from '../../types/product';

import styles from './styles.module.scss';

interface ProductAttributesProps {
  product: Product;
  lastRef?: RefObject<HTMLDivElement>;
  finishRef?: RefObject<HTMLDivElement>;
  lines?: number;
}

/**
 * Component that render product attributes
 * Is used on product page
 * @param props ProductAttributesProps
 */
export const ProductAttributes = (props: ProductAttributesProps) => {
  const {
    product: { tags, metadata, store, width, height, length, variants },
  } = props;

  const quantityProductDetails = useMemo(() => {
    if (!variants[0].inventory_quantity) {
      return null;
    }

    return (
      <p className={styles.detailsItem} key={'quantity'}>
        <b className={styles.linkTitle}>Liczba sztuk:</b>{' '}
        {variants[0].inventory_quantity}
      </p>
    );
  }, []);

  const formatedProductDetailsTags = useMemo(() => {
    if (!tags.length) {
      return null;
    }

    const renderTag = (tag: ProductTag, key: number) => {
      const content = (
        <Link
          href={`/tag-produktu/${_.kebabCase(tag.value)}`}
          className={styles.link}
          key={tag.id}
        >
          {tag.value}
        </Link>
      );

      if (key === tags.length - 1) {
        return content;
      }

      return (
        <Fragment key={tag.id}>
          {content}
          {', '}
        </Fragment>
      );
    };

    return (
      <p className={styles.detailsItem} key={'tags'}>
        <b className={styles.linkTitle}>Tagi:</b> {tags.map(renderTag)}
      </p>
    );
  }, []);

  const formatedProductDetails = useMemo(() => {
    if (!metadata) {
      return null;
    }

    const rowsArray: JSX.Element[] = [];
    const attributesToShow: Record<string, string | boolean> = {};

    if (store.city) {
      attributesToShow.city = store.city;
    }

    attributesToShow['Wysokość'] = metadata.attributes['Wysokość'] ?? height;
    attributesToShow['Szerokość'] = attributesToShow['Szerokość'] ?? width;
    attributesToShow['Głębokość'] = attributesToShow['Głębokość'] ?? length;
    attributesToShow['is_klasyki'] = metadata.klasyki['is_klasyki'] ?? false;
    delete metadata.attributes['city'];

    Object.entries(attributesToShow).forEach(([name, value], index) => {
      const key = metadataKeyMap[name as keyof typeof metadataKeyMap] ?? name;

      if (
        (typeof value === 'string' && value && value !== '-') ||
        (typeof value === 'number' && value)
      ) {
        rowsArray.push(
          <p className={styles.detailsItem} key={`${key}-${index}`}>
            <b>{key}:</b> {value}
          </p>,
        );
      }
    });

    // Color attribute
    const colors = metadata?.attributes?.colors as string[];
    if (colors?.length) {
      const values = colors?.reduce<string[]>((acc, v) => {
        if (v) {
          acc.push(colorsTranslations[v as keyof typeof colorsTranslations]);
        }

        return acc;
      }, []);

      rowsArray.push(
        <p className={styles.detailsItem} key={'color'}>
          <b>Kolor:</b> {values.join(', ')}
        </p>,
      );
    }

    for (const [name, value] of Object.entries(metadata.attributes)) {
      // Default values map key or just key of object
      const key: string =
        metadataKeyMap[name as keyof typeof metadataKeyMap] ?? name;

      // Array values
      if (Array.isArray(value) && value.length) {
        if (key === 'Stan') {
          continue;
        }

        const values = value.reduce((acc, v) => {
          if (v) {
            acc.push(v);
          }

          return acc;
        }, []);

        if (values.length) {
          const separator = key === 'Datowanie' ? ' - ' : ', ';

          rowsArray.push(
            <p className={styles.detailsItem} key={key}>
              <b>{key}:</b> {values.join(separator)}
            </p>,
          );
        }
        continue;
      }

      // Boolean values
      if (typeof value === 'boolean') {
        if (key === 'is_klasyki') {
          continue;
        }

        rowsArray.push(
          <p className={styles.detailsItem} key={key}>
            <b>{key}:</b> {value ? 'Tak' : 'Nie'}
          </p>,
        );

        continue;
      }

      // String  value
      if (
        (typeof value === 'string' && value && value !== '-') ||
        (typeof value === 'number' && value)
      ) {
        if (key === 'Stan') {
          continue;
        }

        rowsArray.push(
          <p className={styles.detailsItem} key={key}>
            <b>{key}:</b> {value}
          </p>,
        );
      }
    }

    const quantity = quantityProductDetails;
    const tags = formatedProductDetailsTags;

    if (quantity) {
      rowsArray.push(quantity);
    }

    if (tags) {
      rowsArray.push(tags);
    }

    return rowsArray.map((row, index) => {
      if (!props.lines) return row;
      let ref = null;
      if (index === rowsArray.length - 1) {
        ref = props.finishRef;
      }
      // Index starts at 0, but we have first line out of array so we need to add 2
      const indexOffset = 2;
      if (index === props.lines - indexOffset) {
        ref = props.lastRef;
      }
      return (
        <div key={row.key} ref={ref}>
          {row}
        </div>
      );
    });
  }, [
    formatedProductDetailsTags,
    height,
    length,
    metadata,
    props.finishRef,
    props.lastRef,
    props.lines,
    quantityProductDetails,
    store.city,
    width,
  ]);

  if (!formatedProductDetails) {
    return <></>;
  }

  return <>{formatedProductDetails}</>;
};
